import { LOADING_SCREEN_TIMEOUT } from 'config/constants';
import { useEffect, useLayoutEffect, useRef, useState, createContext } from 'react';
import useAuth from 'hooks/useAuth';

import { useSessionStorage } from 'hooks/useStorage';
import { WithApolloContext } from 'modules/Apollo/types';

export type LoadingScreenResult = {
  showLoadingScreen: boolean;
  didShowLoadingScreen: boolean;
};

export const LoadingScreenContext: React.Context<LoadingScreenResult> =
  createContext<LoadingScreenResult>({
    showLoadingScreen: false,
    didShowLoadingScreen: false,
  });

export const useLoadingScreen = ({ context }: WithApolloContext): LoadingScreenResult => {
  const { isAuthenticated } = useAuth({ context });
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [didShowLoadingScreen, setDidShowLoadingScreen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useLayoutEffect(() => {
    if (!isAuthenticated) return;

    // Only show loading screen if we have not done so yet
    const hasShown = sessionStorage.getItem('loading-screen') === 'true';

    if (!hasShown) {
      sessionStorage.setItem('loading-screen', 'true');
      setShowLoadingScreen(true);
      setDidShowLoadingScreen(true);

      timeoutRef.current = setTimeout(() => {
        setShowLoadingScreen(false);
      }, LOADING_SCREEN_TIMEOUT);
    } else {
      setDidShowLoadingScreen(true);
      setShowLoadingScreen(false);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [isAuthenticated]);

  return {
    showLoadingScreen,
    didShowLoadingScreen,
  };
};

export const usePostLoadState = ({
  enabled = true,
  context,
}: { enabled: boolean } & WithApolloContext): boolean => {
  const [loadingScreen] = useSessionStorage('loading-screen', null);
  const [postLoadState, setPostLoadState] = useState(loadingScreen != null);
  const { isAuthenticated } = useAuth({ context });

  useEffect(() => {
    if (!enabled) return;

    let timeout;

    if (isAuthenticated) {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setPostLoadState(true);
      }, LOADING_SCREEN_TIMEOUT);
    } else {
      setPostLoadState(false);
      clearTimeout(timeout);
    }
  }, [isAuthenticated, enabled]);

  return enabled ? postLoadState : true;
};
